import { TraplogDashboard } from '@hortplus/traplog-react';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { storesContext } from '../../stores/storesContext';
import BlockScanningDashboard from '../block_scanning/dashboard/BlockScanningDashboard';
import PageTitle from '../helpers/PageTitle';
import IrrigationDashboard from '../irrigation/IrrigationDashboard';
import { enrichSites } from '../irrigation/imsEnrichment';

const ClientDashboard = () => {
  const userStore = useContext(storesContext);
  const history = useHistory();
  const [display, setDisplay] = useState('trapping');

  // Enriched properties for the irrigation dashboard map and card labels.
  // Null means enrichment has not yet been attempted or is in flight —
  // IrrigationDashboard falls back to the raw properties array in that case.
  const [enrichedProperties, setEnrichedProperties] = useState(null);
  const [enrichmentError, setEnrichmentError] = useState(null);

  useEffect(() => {
    const properties = userStore.propertiesStore.properties;
    const allSites = properties.flatMap((p) => p.irrigation_monitoring_sites);
    if (allSites.length === 0) return;

    setEnrichedProperties(null);
    setEnrichmentError(null);

    enrichSites(allSites, userStore.bearerToken, userStore.selectedDatabase)
      .then((enriched) => {
        const byId = Object.fromEntries(enriched.map((s) => [s.id, s]));
        setEnrichedProperties(
          properties.map((p) => ({
            ...p,
            irrigation_monitoring_sites: p.irrigation_monitoring_sites.map(
              (site) => ({ ...site, ...(byId[site.id] ?? {}) })
            )
          }))
        );
      })
      .catch(() => {
        setEnrichmentError(
          'Could not load site details. Map pins and site names may be incomplete.'
        );
      });
  }, [
    userStore.propertiesStore.properties,
    userStore.bearerToken,
    userStore.selectedDatabase
  ]);

  const goToTrapping = async (propertyId) => {
    await userStore.propertiesStore.selectProperty(
      propertyId,
      userStore.selectedDatabase,
      userStore.bearerToken,
      userStore.refreshCallback
    );
    history.push('/pest-disease/trapping-overview');
  };

  useEffect(() => {
    if (localStorage.getItem('selectedDashboardTab')) {
      setDisplay(localStorage.getItem('selectedDashboardTab'));
    }
  }, []);

  const dashboardDisplay = (display) => {
    switch (display) {
      case 'trapping':
        return (
          <TraplogDashboard
            selectedDatabase={userStore.selectedDatabase}
            bearerToken={userStore.bearerToken}
            refreshBearerToken={userStore.refresh}
            propertiesStore={userStore.propertiesStore}
            goToTrapping={goToTrapping}
          />
        );
      case 'irrigation':
        return (
          <>
            {enrichmentError ? (
              <Alert variant='warning' className='mt-2'>
                {enrichmentError}
              </Alert>
            ) : null}
            <IrrigationDashboard
              properties={enrichedProperties ?? userStore.propertiesStore.properties}
              loading={userStore.propertiesStore.loading && userStore.loading}
            />
          </>
        );
      case 'scanning':
        return (
          <BlockScanningDashboard
            loading={userStore.propertiesStore.loading && userStore.loading}
          />
        );
      default:
        return undefined;
    }
  };

  return (
    <Container className='p-0'>
      <Row className='mb-4'>
        <Col>
          <PageTitle title={'Monitoring Map'} />
        </Col>
      </Row>
      <Container className='p-0 d-flex justify-content-center mb-2'>
        <Button
          variant={display === 'trapping' ? 'primary' : 'light'}
          onClick={() => {
            localStorage.setItem('selectedDashboardTab', 'trapping');
            setDisplay(localStorage.getItem('selectedDashboardTab'));
          }}
          className='mr-2'
        >
          Trapping
        </Button>
        <Button
          variant={display === 'irrigation' ? 'primary' : 'light'}
          onClick={() => {
            localStorage.setItem('selectedDashboardTab', 'irrigation');
            setDisplay(localStorage.getItem('selectedDashboardTab'));
          }}
          className='mr-2'
        >
          Irrigation
        </Button>
        <Button
          variant={display === 'scanning' ? 'primary' : 'light'}
          onClick={() => {
            localStorage.setItem('selectedDashboardTab', 'scanning');
            setDisplay(localStorage.getItem('selectedDashboardTab'));
          }}
        >
          Scanning
        </Button>
      </Container>
      {dashboardDisplay(display)}
    </Container>
  );
};

export default observer(ClientDashboard);

